import React, { useId } from "react";
import type { PdfFormFieldEditorContext } from "./types";
import { getPdfFormTextValue, isPdfFormCheckboxChecked } from "./field-value";

/** Native controlled inputs keep consumer values and keyboard behavior predictable. */
export function PdfFormFieldEditor({
  field,
  readOnly,
  onChange,
  onDelete,
}: PdfFormFieldEditorContext) {
  const id = useId();
  return (
    <div className="pdf-form-renderer__editor">
      <h3>Field details</h3>
      <label htmlFor={`${id}-question`}>Question for this box</label>
      <input
        id={`${id}-question`}
        value={field.label}
        readOnly={readOnly}
        onChange={(event) => onChange({ label: event.target.value })}
      />
      <label htmlFor={`${id}-format`}>Answer format (optional)</label>
      <input
        id={`${id}-format`}
        value={field.answerFormat ?? ""}
        readOnly={readOnly}
        placeholder="e.g. MM/DD/YYYY"
        onChange={(event) => onChange({ answerFormat: event.target.value })}
      />
      {field.type === "checkbox" ? (
        <>
          <label className="pdf-form-renderer__checkbox-label">
            <input
              type="checkbox"
              checked={isPdfFormCheckboxChecked(field)}
              disabled={readOnly}
              onChange={(event) =>
                onChange({
                  value: event.target.checked
                    ? field.options?.find((option) => option.trim() !== "") ??
                      true
                    : "",
                })
              }
            />{" "}
            Checked
          </label>
          <label htmlFor={`${id}-options`}>Checked values (one per line)</label>
          <textarea
            id={`${id}-options`}
            value={field.options?.join("\n") ?? ""}
            readOnly={readOnly}
            onChange={(event) =>
              onChange({ options: event.target.value.split("\n") })
            }
          />
        </>
      ) : (
        <>
          <label htmlFor={`${id}-answer`}>Answer</label>
          <textarea
            id={`${id}-answer`}
            value={getPdfFormTextValue(field)}
            readOnly={readOnly}
            onChange={(event) => onChange({ value: event.target.value })}
          />
        </>
      )}
      <fieldset disabled={readOnly}>
        <legend>Rectangle (PDF points)</legend>
        <div className="pdf-form-renderer__coordinates">
          {(["x0", "y0", "x1", "y1"] as const).map((name, index) => (
            <label key={name}>
              {name}
              <input
                type="number"
                step="0.1"
                value={Number(field.rect[index]!.toFixed(2))}
                onChange={(event) => {
                  const next = [...field.rect] as [
                    number,
                    number,
                    number,
                    number
                  ];
                  next[index] = event.target.valueAsNumber;
                  if (
                    next.every(Number.isFinite) &&
                    next[2] > next[0] &&
                    next[3] > next[1]
                  )
                    onChange({ rect: next });
                }}
              />
            </label>
          ))}
        </div>
      </fieldset>
      {!readOnly && (
        <button
          type="button"
          className="pdf-form-renderer__delete"
          onClick={onDelete}
        >
          Delete field
        </button>
      )}
    </div>
  );
}
